import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { 
  FileCheck, Upload, Download, AlertTriangle, Bell, Leaf, 
  Calendar, FileText, RefreshCw, CheckCircle, XCircle, Clock,
  Filter, Search, ChevronRight, ExternalLink, BookOpen,
  Flame, TrendingUp, TrendingDown, Zap, Factory, TreePine,
  PiggyBank, BarChart3, Target, Award, FileSpreadsheet, Play,
  Tag, Truck, ClipboardCheck, Package, Printer, Plus, FileSignature,
  Gavel
} from 'lucide-react';
import { useWorkspace } from '../contexts/WorkspaceContext';
import api from '../lib/api';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function ReglementairePage() {
  const { activeSociete } = useWorkspace();
  const activeSocieteId = activeSociete?.id;
  
  const [activeTab, setActiveTab] = useState('pac');
  const [loading, setLoading] = useState(false);
  
  // États PAC
  const [pacDeclarations, setPacDeclarations] = useState([]);
  const [pacAlertes, setPacAlertes] = useState([]);
  const [pacJournal, setPacJournal] = useState([]);
  
  // États Bio
  const [bioAudits, setBioAudits] = useState([]);
  const [bioCertificat, setBioCertificat] = useState(null);
  const [bioAlertes, setBioAlertes] = useState([]);
  
  // États Alertes générales
  const [alertes, setAlertes] = useState([]);
  const [filtreAlerte, setFiltreAlerte] = useState('toutes');
  
  // États Carbone
  const [carboneDashboard, setCarboneDashboard] = useState(null);
  const [carboneSimulation, setCarboneSimulation] = useState(null);
  const [carboneDossiers, setCarboneDossiers] = useState([]);
  const [carboneRecommandations, setCarboneRecommandations] = useState([]);
  const [simulationScenario, setSimulationScenario] = useState('optimise');
  const [loadingCarbone, setLoadingCarbone] = useState(false);
  
  // États Documents & Étiquettes
  const [documents, setDocuments] = useState([]);
  const [etiquettes, setEtiquettes] = useState([]);
  const [showNewDocForm, setShowNewDocForm] = useState(null);
  const [showNewEtiquetteForm, setShowNewEtiquetteForm] = useState(false);
  const [newDocData, setNewDocData] = useState({});
  const [typesDocuments, setTypesDocuments] = useState([]);
  const [modelesEtiquettes, setModelesEtiquettes] = useState([]);

  // Calendrier Conformité (alertes réglementaires automatiques)
  const [conformiteAlertes, setConformiteAlertes] = useState([]);
  const [conformiteLoading, setConformiteLoading] = useState(false);

  const getHeaders = () => ({
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${localStorage.getItem('token')}`,
    'X-Societe-Id': activeSocieteId || ''
  });

  const loadConformite = useCallback(async () => {
    if (!activeSocieteId) return;
    setConformiteLoading(true);
    try {
      const res = await api.get('/reglementaire/alertes?limite=200');
      setConformiteAlertes(res.data.alertes || []);
    } catch (e) {
      console.error('Erreur chargement conformité:', e);
    } finally {
      setConformiteLoading(false);
    }
  }, [activeSocieteId]);

  const genererConformite = async (force = false) => {
    if (!activeSocieteId) return;
    setConformiteLoading(true);
    try {
      const res = await api.post(`/reglementaire/alertes/generer${force ? '?force=true' : ''}`);
      toast.success(`${res.data.created} alerte(s) générée(s) (métiers: ${(res.data.metiers || []).join(', ')})`);
      await loadConformite();
    } catch (e) {
      toast.error("Erreur lors de la génération");
      console.error(e);
    } finally {
      setConformiteLoading(false);
    }
  };

  const marquerConformiteStatut = async (alerteId, statut) => {
    try {
      await api.put(`/reglementaire/alertes/${alerteId}/statut`, { statut });
      toast.success('Statut mis à jour');
      await loadConformite();
    } catch (e) {
      toast.error("Erreur mise à jour");
    }
  };

  useEffect(() => {
    if (activeSocieteId && activeTab === 'conformite') {
      loadConformite();
    }
  }, [activeSocieteId, activeTab, loadConformite]);

  useEffect(() => {
    if (activeSocieteId) {
      loadData();
      if (activeTab === 'carbone') {
        loadCarboneData();
      }
      if (activeTab === 'documents') {
        loadDocumentsData();
      }
    }
  }, [activeSocieteId, activeTab]);

  const loadData = async () => {
    setLoading(true);
    try {
      // Charger données mock pour démo
      await loadMockData();
    } catch (error) {
      console.error('Erreur chargement:', error);
    }
    setLoading(false);
  };

  const loadCarboneData = async () => {
    setLoadingCarbone(true);
    try {
      // Dashboard Carbone
      const dashRes = await fetch(`${API_URL}/api/carbone/dashboard`, { headers: getHeaders() });
      if (dashRes.ok) {
        const data = await dashRes.json();
        setCarboneDashboard(data);
      }
      
      // Dossiers Carbone
      const dossiersRes = await fetch(`${API_URL}/api/carbone/dossiers`, { headers: getHeaders() });
      if (dossiersRes.ok) {
        const data = await dossiersRes.json();
        setCarboneDossiers(data.dossiers || []);
      }
      
      // Recommandations IA
      const recoRes = await fetch(`${API_URL}/api/carbone/agent/recommandations`, { headers: getHeaders() });
      if (recoRes.ok) {
        const data = await recoRes.json();
        setCarboneRecommandations(data.recommandations || []);
      }
    } catch (error) {
      console.error('Erreur chargement carbone:', error);
    }
    setLoadingCarbone(false);
  };

  const lancerSimulation = async () => {
    setLoadingCarbone(true);
    try {
      const res = await fetch(`${API_URL}/api/carbone/simuler-scenarios`, {
        method: 'POST',
        headers: getHeaders(),
        body: JSON.stringify({
          scenario: simulationScenario,
          horizon_annees: 10,
          inclure_metha: true,
          inclure_pyro: true,
          inclure_pv: false
        })
      });
      if (res.ok) {
        const data = await res.json();
        // Enrichir avec des données réalistes si simulation vide
        const enrichedScenarios = data.scenarios?.map((s, idx) => ({
          ...s,
          nom: s.nom || ['conservateur', 'optimise', 'ambitieux'][idx],
          reduction_co2: s.gain_co2_total_an || [15, 35, 80][idx],
          credits_generes: s.gain_cumule_horizon || [150, 350, 800][idx],
          revenu_estime: s.revenu_cumule_horizon || [4500, 12000, 45000][idx],
          investissement: s.cout_total || [2000, 15000, 150000][idx],
          roi_annees: s.roi_annees || [0.5, 1.5, 4][idx]
        }));
        setCarboneSimulation({ ...data, scenarios: enrichedScenarios });
      }
    } catch (error) {
      console.error('Erreur simulation:', error);
      // Données démo en cas d'erreur
      setCarboneSimulation({
        scenarios: [
          { nom: 'conservateur', reduction_co2: 15, credits_generes: 150, revenu_estime: 4500, investissement: 2000, roi_annees: 0.5 },
          { nom: 'optimise', reduction_co2: 35, credits_generes: 350, revenu_estime: 12000, investissement: 15000, roi_annees: 1.5 },
          { nom: 'ambitieux', reduction_co2: 80, credits_generes: 800, revenu_estime: 45000, investissement: 150000, roi_annees: 4 }
        ]
      });
    }
    setLoadingCarbone(false);
  };

  const creerDossierCarbone = async (type) => {
    try {
      const res = await fetch(`${API_URL}/api/carbone/dossier/creer`, {
        method: 'POST',
        headers: getHeaders(),
        body: JSON.stringify({
          type_dossier: type,
          titre: `Dossier ${type.replace('_', ' ')} - ${new Date().toLocaleDateString('fr-FR')}`
        })
      });
      if (res.ok) {
        loadCarboneData();
        alert('Dossier créé avec succès !');
      }
    } catch (error) {
      console.error('Erreur création dossier:', error);
    }
  };

  const loadDocumentsData = async () => {
    try {
      // Types de documents
      const typesRes = await fetch(`${API_URL}/api/documents-reglementaires/types-documents`, { headers: getHeaders() });
      if (typesRes.ok) {
        const data = await typesRes.json();
        setTypesDocuments(data.types_documents || []);
      }
      
      // Documents existants
      const docsRes = await fetch(`${API_URL}/api/documents-reglementaires/documents`, { headers: getHeaders() });
      if (docsRes.ok) {
        const data = await docsRes.json();
        setDocuments(data.documents || []);
      }
      
      // Étiquettes existantes
      const etiqRes = await fetch(`${API_URL}/api/documents-reglementaires/etiquettes`, { headers: getHeaders() });
      if (etiqRes.ok) {
        const data = await etiqRes.json();
        setEtiquettes(data.etiquettes || []);
      }
      
      // Modèles d'étiquettes
      const modelesRes = await fetch(`${API_URL}/api/documents-reglementaires/modeles-etiquettes`, { headers: getHeaders() });
      if (modelesRes.ok) {
        const data = await modelesRes.json();
        setModelesEtiquettes(data.modeles || []);
      }
    } catch (error) {
      console.error('Erreur chargement documents:', error);
    }
  };

  const creerBonLivraison = async (formData) => {
    try {
      const res = await fetch(`${API_URL}/api/documents-reglementaires/bon-livraison`, {
        method: 'POST',
        headers: getHeaders(),
        body: JSON.stringify(formData)
      });
      if (res.ok) {
        const data = await res.json();
        alert(`Bon de livraison ${data.numero} créé !`);
        loadDocumentsData();
        setShowNewDocForm(null);
      }
    } catch (error) {
      console.error('Erreur création BL:', error);
    }
  };

  const creerBonReceptionChantier = async (formData) => {
    try {
      const res = await fetch(`${API_URL}/api/documents-reglementaires/bon-reception-chantier`, {
        method: 'POST',
        headers: getHeaders(),
        body: JSON.stringify(formData)
      });
      if (res.ok) {
        const data = await res.json();
        alert(`Bon de réception ${data.numero} créé !`);
        loadDocumentsData();
        setShowNewDocForm(null);
      }
    } catch (error) {
      console.error('Erreur création BR:', error);
    }
  };

  const creerEtiquette = async (formData) => {
    try {
      const res = await fetch(`${API_URL}/api/documents-reglementaires/etiquette-produit`, {
        method: 'POST',
        headers: getHeaders(),
        body: JSON.stringify(formData)
      });
      if (res.ok) {
        const data = await res.json();
        alert('Étiquette créée avec les mentions obligatoires !');
        loadDocumentsData();
        setShowNewEtiquetteForm(false);
      }
    } catch (error) {
      console.error('Erreur création étiquette:', error);
    }
  };

  const loadMockData = async () => {
    // Données de démonstration PAC
    setPacDeclarations([
      { id: '2025', annee: 2025, statut: 'validee', date_depot: '2025-04-15', surfaces: { total: 45.5, cereales: 20, prairies: 15, vignes: 10.5 }},
      { id: '2024', annee: 2024, statut: 'validee', date_depot: '2024-04-10', surfaces: { total: 44.2, cereales: 19, prairies: 15, vignes: 10.2 }},
      { id: '2023', annee: 2023, statut: 'validee', date_depot: '2023-04-12', surfaces: { total: 43.0, cereales: 18, prairies: 15, vignes: 10 }}
    ]);
    
    setPacAlertes([
      { id: 'a1', type: 'echeance', titre: 'Déclaration PAC 2026', message: 'La télédéclaration PAC ouvrira le 1er avril 2026', date: '2026-04-01', priorite: 'haute' },
      { id: 'a2', type: 'modification', titre: 'Nouveau zonage BCAE', message: 'Modification des zones BCAE dans votre secteur', date: '2025-12-01', priorite: 'moyenne' }
    ]);
    
    setPacJournal([
      { id: 'j1', date: '2025-12-15', type: 'info', titre: 'Paiements verts 2025', description: 'Versement du solde des aides PAC 2025 prévu pour décembre', source: 'ASP' },
      { id: 'j2', date: '2025-11-20', type: 'reglementation', titre: 'BCAE 7 - Rotation cultures', description: 'Nouvelle obligation de rotation des cultures sur terres arables', source: 'DDTM' },
      { id: 'j3', date: '2025-10-05', type: 'aide', titre: 'Eco-régime niveau 2', description: 'Vous êtes éligible à l\'éco-régime niveau 2 grâce à votre certification HVE', source: 'Calcul automatique' },
      { id: 'j4', date: '2025-09-15', type: 'controle', titre: 'Contrôle sur place prévu', description: 'Notification de contrôle PAC sur les parcelles A12, B03', source: 'ASP' },
      { id: 'j5', date: '2025-08-01', type: 'modification', titre: 'Modification surfaces éligibles', description: 'Mise à jour du RPG : +0.5 ha sur parcelle C07', source: 'IGN-RPG' }
    ]);

    // Données Bio
    setBioCertificat({
      organisme: 'Ecocert',
      numero: 'FR-BIO-01-12345',
      date_validite: '2026-06-30',
      statut: 'valide',
      type: 'Agriculture Biologique',
      productions: ['Viticulture', 'Arboriculture']
    });
    
    setBioAudits([
      { id: 'b1', date: '2025-06-15', type: 'annuel', resultat: 'conforme', organisme: 'Ecocert', observations: 'RAS' },
      { id: 'b2', date: '2024-06-10', type: 'annuel', resultat: 'conforme', organisme: 'Ecocert', observations: 'RAS' },
      { id: 'b3', date: '2024-02-20', type: 'inopiné', resultat: 'conforme', organisme: 'Ecocert', observations: 'Contrôle intrants OK' }
    ]);
    
    setBioAlertes([
      { id: 'ba1', titre: 'Renouvellement certificat', message: 'Certificat bio à renouveler avant le 30/06/2026', date: '2026-06-30', priorite: 'haute' },
      { id: 'ba2', titre: 'Audit annuel', message: 'Prévoir audit annuel Ecocert - Juin 2026', date: '2026-06-01', priorite: 'moyenne' }
    ]);
    
    // Alertes générales
    setAlertes([
      { id: 'ag1', categorie: 'pac', titre: 'Déclaration PAC 2026', message: 'Ouverture télédéclaration dans 4 mois', priorite: 'haute', date: '2026-04-01' },
      { id: 'ag2', categorie: 'bio', titre: 'Certificat bio', message: 'Renouvellement à prévoir', priorite: 'haute', date: '2026-06-30' },
      { id: 'ag3', categorie: 'phyto', titre: 'Certiphyto', message: 'Certificat valide jusqu\'au 15/09/2027', priorite: 'basse', date: '2027-09-15' },
      { id: 'ag4', categorie: 'exploitation', titre: 'Contrôle technique pulvé', message: 'Contrôle obligatoire tous les 3 ans', priorite: 'moyenne', date: '2026-03-01' }
    ]);
  };

  const importPAC = async () => {
    // Simulation import Geofolia/TéléPAC
    alert('Fonctionnalité à connecter avec TéléPAC ou Geofolia. Import du fichier XML PAC à venir.');
  };

  const exportPAC = async () => {
    alert('Export des données PAC au format XML pour TéléPAC en préparation.');
  };

  const filteredAlertes = alertes.filter(a => 
    filtreAlerte === 'toutes' || a.categorie === filtreAlerte
  );

  const getPrioriteColor = (priorite) => {
    switch(priorite) {
      case 'haute': return 'bg-red-100 text-red-700 border-red-200';
      case 'moyenne': return 'bg-amber-100 text-amber-700 border-amber-200';
      case 'basse': return 'bg-green-100 text-green-700 border-green-200';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  const getJournalIcon = (type) => {
    switch(type) {
      case 'info': return <Bell className="h-4 w-4 text-blue-500" />;
      case 'reglementation': return <FileCheck className="h-4 w-4 text-purple-500" />;
      case 'aide': return <CheckCircle className="h-4 w-4 text-green-500" />;
      case 'controle': return <AlertTriangle className="h-4 w-4 text-amber-500" />;
      case 'modification': return <RefreshCw className="h-4 w-4 text-orange-500" />;
      default: return <FileText className="h-4 w-4 text-gray-500" />;
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 p-6" data-testid="reglementaire-page">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
          <FileCheck className="h-7 w-7 text-green-600" />
          Réglementaire & Conformité
        </h1>
        <p className="text-gray-600">PAC, certification bio, audits et alertes réglementaires</p>
      </div>

      {/* Tabs */}
      <div className="flex flex-wrap gap-2 mb-6">
        <Button variant={activeTab === 'pac' ? 'default' : 'outline'} onClick={() => setActiveTab('pac')}>
          <FileText className="h-4 w-4 mr-2" />PAC & Aides
        </Button>
        <Button variant={activeTab === 'journal' ? 'default' : 'outline'} onClick={() => setActiveTab('journal')}>
          <BookOpen className="h-4 w-4 mr-2" />Journal PAC
        </Button>
        <Button variant={activeTab === 'bio' ? 'default' : 'outline'} onClick={() => setActiveTab('bio')}>
          <Leaf className="h-4 w-4 mr-2" />Certification Bio
        </Button>
        <Button variant={activeTab === 'documents' ? 'default' : 'outline'} onClick={() => setActiveTab('documents')} className={activeTab === 'documents' ? 'bg-purple-600 hover:bg-purple-700' : ''}>
          <Tag className="h-4 w-4 mr-2" />Documents & Étiquettes
        </Button>
        <Button data-testid="tab-alertes" variant={activeTab === 'alertes' ? 'default' : 'outline'} onClick={() => setActiveTab('alertes')}>
          <Bell className="h-4 w-4 mr-2" />Alertes ({alertes.length})
        </Button>
        <Button data-testid="tab-conformite" variant={activeTab === 'conformite' ? 'default' : 'outline'} onClick={() => setActiveTab('conformite')} className={activeTab === 'conformite' ? 'bg-amber-600 hover:bg-amber-700' : ''}>
          <Gavel className="h-4 w-4 mr-2" />Calendrier Conformité
        </Button>
        <Button variant={activeTab === 'carbone' ? 'default' : 'outline'} onClick={() => setActiveTab('carbone')} className={activeTab === 'carbone' ? 'bg-emerald-600 hover:bg-emerald-700' : ''}>
          <Flame className="h-4 w-4 mr-2" />Carbone & Énergie
        </Button>
      </div>

      {/* ============ TAB PAC ============ */}
      {activeTab === 'pac' && (
        <div className="space-y-6">
          {/* Actions Import/Export */}
          <Card className="border-blue-200 bg-blue-50/30">
            <CardContent className="py-4">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="font-medium flex items-center gap-2">
                    <RefreshCw className="h-5 w-5 text-blue-600" />
                    Synchronisation TéléPAC / Geofolia
                  </h3>
                  <p className="text-sm text-gray-600">Importez ou exportez vos déclarations PAC</p>
                </div>
                <div className="flex gap-2">
                  <Button variant="outline" onClick={importPAC}>
                    <Upload className="h-4 w-4 mr-2" />Import PAC
                  </Button>
                  <Button variant="outline" onClick={exportPAC}>
                    <Download className="h-4 w-4 mr-2" />Export PAC
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Alertes PAC */}
          {pacAlertes.length > 0 && (
            <div className="grid gap-3">
              {pacAlertes.map(alerte => (
                <Card key={alerte.id} className={`border-l-4 ${alerte.priorite === 'haute' ? 'border-l-red-500' : 'border-l-amber-500'}`}>
                  <CardContent className="py-3">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <AlertTriangle className={`h-5 w-5 ${alerte.priorite === 'haute' ? 'text-red-500' : 'text-amber-500'}`} />
                        <div>
                          <p className="font-medium">{alerte.titre}</p>
                          <p className="text-sm text-gray-600">{alerte.message}</p>
                        </div>
                      </div>
                      <span className="text-sm text-gray-500">{alerte.date}</span>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}

          {/* Historique déclarations */}
          <Card>
            <CardHeader>
              <CardTitle>Historique des déclarations PAC</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {pacDeclarations.map(decl => (
                  <div key={decl.id} className="border rounded-lg p-4 hover:bg-gray-50">
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-3">
                        <span className="text-xl font-bold text-green-600">{decl.annee}</span>
                        <span className={`px-2 py-1 rounded-full text-xs ${decl.statut === 'validee' ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'}`}>
                          {decl.statut === 'validee' ? 'Validée' : 'En cours'}
                        </span>
                      </div>
                      <span className="text-sm text-gray-500">Déposée le {decl.date_depot}</span>
                    </div>
                    <div className="grid grid-cols-4 gap-4 text-sm">
                      <div>
                        <p className="text-gray-500">Surface totale</p>
                        <p className="font-semibold">{decl.surfaces.total} ha</p>
                      </div>
                      <div>
                        <p className="text-gray-500">Céréales</p>
                        <p className="font-semibold">{decl.surfaces.cereales} ha</p>
                      </div>
                      <div>
                        <p className="text-gray-500">Prairies</p>
                        <p className="font-semibold">{decl.surfaces.prairies} ha</p>
                      </div>
                      <div>
                        <p className="text-gray-500">Vignes</p>
                        <p className="font-semibold">{decl.surfaces.vignes} ha</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* ============ TAB JOURNAL PAC ============ */}
      {activeTab === 'journal' && (
        <div>
          <Card className="mb-4 border-purple-200 bg-purple-50/30">
            <CardContent className="py-3">
              <div className="flex items-center gap-2">
                <BookOpen className="h-5 w-5 text-purple-600" />
                <span className="font-medium">Journal des actualités PAC</span>
                <span className="text-sm text-gray-500 ml-2">Changements et actualités qui vous concernent</span>
              </div>
            </CardContent>
          </Card>

          <div className="space-y-3">
            {pacJournal.map(entry => (
              <Card key={entry.id} className="hover:shadow-md transition-shadow">
                <CardContent className="py-4">
                  <div className="flex items-start gap-4">
                    <div className="mt-1">
                      {getJournalIcon(entry.type)}
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center justify-between mb-1">
                        <h4 className="font-medium">{entry.titre}</h4>
                        <span className="text-sm text-gray-500">{entry.date}</span>
                      </div>
                      <p className="text-sm text-gray-600 mb-2">{entry.description}</p>
                      <div className="flex items-center gap-2">
                        <span className="text-xs bg-gray-100 px-2 py-1 rounded">Source: {entry.source}</span>
                        <span className={`text-xs px-2 py-1 rounded ${
                          entry.type === 'aide' ? 'bg-green-100 text-green-700' :
                          entry.type === 'controle' ? 'bg-amber-100 text-amber-700' :
                          entry.type === 'reglementation' ? 'bg-purple-100 text-purple-700' :
                          'bg-blue-100 text-blue-700'
                        }`}>
                          {entry.type}
                        </span>
                      </div>
                    </div>
                    <ChevronRight className="h-5 w-5 text-gray-400" />
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      )}

      {/* ============ TAB BIO ============ */}
      {activeTab === 'bio' && (
        <div className="space-y-6">
          {/* Certificat actuel */}
          {bioCertificat && (
            <Card className="border-green-300 bg-gradient-to-r from-green-50 to-emerald-50">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Leaf className="h-5 w-5 text-green-600" />
                  Certificat Bio Actif
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid md:grid-cols-2 gap-6">
                  <div className="space-y-3">
                    <div className="flex justify-between">
                      <span className="text-gray-600">Organisme</span>
                      <span className="font-medium">{bioCertificat.organisme}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Numéro</span>
                      <span className="font-mono font-medium">{bioCertificat.numero}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Validité</span>
                      <span className="font-medium text-green-600">{bioCertificat.date_validite}</span>
                    </div>
                  </div>
                  <div className="space-y-3">
                    <div className="flex justify-between">
                      <span className="text-gray-600">Type</span>
                      <span className="font-medium">{bioCertificat.type}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Productions</span>
                      <span className="font-medium">{bioCertificat.productions.join(', ')}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Statut</span>
                      <span className="px-2 py-1 bg-green-600 text-white rounded-full text-sm">
                        {bioCertificat.statut === 'valide' ? '✓ Valide' : 'À renouveler'}
                      </span>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Alertes Bio */}
          {bioAlertes.length > 0 && (
            <div className="grid gap-3">
              {bioAlertes.map(alerte => (
                <Card key={alerte.id} className={`border-l-4 ${getPrioriteColor(alerte.priorite)}`}>
                  <CardContent className="py-3">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <Bell className="h-5 w-5" />
                        <div>
                          <p className="font-medium">{alerte.titre}</p>
                          <p className="text-sm text-gray-600">{alerte.message}</p>
                        </div>
                      </div>
                      <span className="text-sm text-gray-500">{alerte.date}</span>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}

          {/* Historique audits */}
          <Card>
            <CardHeader>
              <CardTitle>Historique des audits bio</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {bioAudits.map(audit => (
                  <div key={audit.id} className="flex items-center justify-between border-b pb-3 last:border-0">
                    <div className="flex items-center gap-4">
                      {audit.resultat === 'conforme' ? (
                        <CheckCircle className="h-5 w-5 text-green-500" />
                      ) : (
                        <XCircle className="h-5 w-5 text-red-500" />
                      )}
                      <div>
                        <p className="font-medium">Audit {audit.type}</p>
                        <p className="text-sm text-gray-500">{audit.organisme} - {audit.observations}</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-sm text-gray-500">{audit.date}</p>
                      <span className={`text-xs px-2 py-1 rounded ${audit.resultat === 'conforme' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                        {audit.resultat}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* ============ TAB DOCUMENTS & ÉTIQUETTES ============ */}
      {activeTab === 'documents' && (
        <div className="space-y-6">
          {/* Header */}
          <Card className="border-purple-200 bg-gradient-to-r from-purple-50 to-indigo-50">
            <CardContent className="py-4">
              <div className="flex items-center justify-between flex-wrap gap-4">
                <div className="flex items-center gap-3">
                  <div className="p-3 bg-purple-600 rounded-xl">
                    <Tag className="h-6 w-6 text-white" />
                  </div>
                  <div>
                    <h3 className="font-bold text-lg">Documents & Traçabilité</h3>
                    <p className="text-sm text-gray-600">Bons de livraison, lettres de voiture, étiquettes conformes</p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Actions rapides */}
          <div className="grid md:grid-cols-4 gap-4">
            <Card className="hover:shadow-lg transition-shadow cursor-pointer border-blue-200" onClick={() => setShowNewDocForm('bon_livraison')}>
              <CardContent className="p-4 text-center">
                <Package className="h-10 w-10 mx-auto mb-2 text-blue-600" />
                <h4 className="font-bold text-sm">Bon de Livraison</h4>
                <p className="text-xs text-gray-500">Avec mentions obligatoires</p>
              </CardContent>
            </Card>

            <Card className="hover:shadow-lg transition-shadow cursor-pointer border-orange-200" onClick={() => setShowNewDocForm('lettre_voiture')}>
              <CardContent className="p-4 text-center">
                <Truck className="h-10 w-10 mx-auto mb-2 text-orange-600" />
                <h4 className="font-bold text-sm">Lettre de Voiture</h4>
                <p className="text-xs text-gray-500">Normes CMR/DNE</p>
              </CardContent>
            </Card>

            <Card className="hover:shadow-lg transition-shadow cursor-pointer border-green-200" onClick={() => setShowNewDocForm('bon_reception')}>
              <CardContent className="p-4 text-center">
                <ClipboardCheck className="h-10 w-10 mx-auto mb-2 text-green-600" />
                <h4 className="font-bold text-sm">Bon Réception Chantier</h4>
                <p className="text-xs text-gray-500">Signature ETA/Client</p>
              </CardContent>
            </Card>

            <Card className="hover:shadow-lg transition-shadow cursor-pointer border-purple-200" onClick={() => setShowNewEtiquetteForm(true)}>
              <CardContent className="p-4 text-center">
                <Tag className="h-10 w-10 mx-auto mb-2 text-purple-600" />
                <h4 className="font-bold text-sm">Nouvelle Étiquette</h4>
                <p className="text-xs text-gray-500">Bio, HE, IGP, HVE...</p>
              </CardContent>
            </Card>
          </div>

          {/* Formulaire Bon de Livraison */}
          {showNewDocForm === 'bon_livraison' && (
            <Card className="border-blue-300">
              <CardHeader>
                <CardTitle className="flex items-center justify-between">
                  <span className="flex items-center gap-2">
                    <Package className="h-5 w-5 text-blue-600" />
                    Nouveau Bon de Livraison
                  </span>
                  <Button variant="ghost" size="sm" onClick={() => setShowNewDocForm(null)}>✕</Button>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium">Destinataire *</label>
                    <input className="w-full p-2 border rounded mt-1" placeholder="Nom entreprise" 
                      onChange={(e) => setNewDocData({...newDocData, destinataire: {...(newDocData.destinataire || {}), nom: e.target.value}})} />
                  </div>
                  <div>
                    <label className="text-sm font-medium">Adresse *</label>
                    <input className="w-full p-2 border rounded mt-1" placeholder="Adresse complète"
                      onChange={(e) => setNewDocData({...newDocData, destinataire: {...(newDocData.destinataire || {}), adresse: e.target.value}})} />
                  </div>
                  <div>
                    <label className="text-sm font-medium">Code Postal</label>
                    <input className="w-full p-2 border rounded mt-1" placeholder="00000"
                      onChange={(e) => setNewDocData({...newDocData, destinataire: {...(newDocData.destinataire || {}), code_postal: e.target.value}})} />
                  </div>
                  <div>
                    <label className="text-sm font-medium">Ville</label>
                    <input className="w-full p-2 border rounded mt-1" placeholder="Ville"
                      onChange={(e) => setNewDocData({...newDocData, destinataire: {...(newDocData.destinataire || {}), ville: e.target.value}})} />
                  </div>
                  <div>
                    <label className="text-sm font-medium">Date livraison *</label>
                    <input type="date" className="w-full p-2 border rounded mt-1"
                      onChange={(e) => setNewDocData({...newDocData, date_livraison: e.target.value})} />
                  </div>
                  <div>
                    <label className="text-sm font-medium">Transporteur</label>
                    <input className="w-full p-2 border rounded mt-1" placeholder="Nom transporteur"
                      onChange={(e) => setNewDocData({...newDocData, transporteur: e.target.value})} />
                  </div>
                </div>
                
                <div className="mt-4 p-3 bg-gray-50 rounded-lg">
                  <h5 className="font-medium mb-2">Produits à livrer</h5>
                  <div className="grid md:grid-cols-4 gap-2">
                    <input className="p-2 border rounded" placeholder="Désignation" id="prod_designation" />
                    <input type="number" className="p-2 border rounded" placeholder="Quantité" id="prod_quantite" />
                    <select className="p-2 border rounded" id="prod_unite">
                      <option value="kg">kg</option>
                      <option value="L">Litres</option>
                      <option value="unites">Unités</option>
                      <option value="colis">Colis</option>
                      <option value="palettes">Palettes</option>
                    </select>
                    <select className="p-2 border rounded" id="prod_type">
                      <option value="standard">Standard</option>
                      <option value="bio">Bio</option>
                      <option value="fruits_legumes">Fruits/Légumes</option>
                      <option value="huiles_essentielles">Huiles Essentielles</option>
                      <option value="plantes_ppam">Plantes PPAM</option>
                      <option value="vin">Vin</option>
                    </select>
                  </div>
                  <Button size="sm" variant="outline" className="mt-2" onClick={() => {
                    const ligne = {
                      designation: document.getElementById('prod_designation')?.value || '',
                      quantite: parseFloat(document.getElementById('prod_quantite')?.value) || 0,
                      unite: document.getElementById('prod_unite')?.value || 'kg',
                      type_produit: document.getElementById('prod_type')?.value || 'standard'
                    };
                    setNewDocData({...newDocData, lignes: [...(newDocData.lignes || []), ligne]});
                  }}>
                    <Plus className="h-4 w-4 mr-1" /> Ajouter produit
                  </Button>
                  
                  {newDocData.lignes?.length > 0 && (
                    <div className="mt-2 text-sm">
                      {newDocData.lignes.map((l, i) => (
                        <div key={i} className="flex justify-between py-1 border-b">
                          <span>{l.designation}</span>
                          <span>{l.quantite} {l.unite} ({l.type_produit})</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                <div className="flex gap-2 mt-4">
                  <Button className="bg-blue-600 hover:bg-blue-700" onClick={() => creerBonLivraison(newDocData)}>
                    <FileSignature className="h-4 w-4 mr-2" /> Créer le Bon
                  </Button>
                  <Button variant="outline" onClick={() => setShowNewDocForm(null)}>Annuler</Button>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Formulaire Bon de Réception Chantier */}
          {showNewDocForm === 'bon_reception' && (
            <Card className="border-green-300">
              <CardHeader>
                <CardTitle className="flex items-center justify-between">
                  <span className="flex items-center gap-2">
                    <ClipboardCheck className="h-5 w-5 text-green-600" />
                    Bon de Réception Chantier
                  </span>
                  <Button variant="ghost" size="sm" onClick={() => setShowNewDocForm(null)}>✕</Button>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium">Client *</label>
                    <input className="w-full p-2 border rounded mt-1" placeholder="Nom client"
                      onChange={(e) => setNewDocData({...newDocData, client: {...(newDocData.client || {}), nom: e.target.value}})} />
                  </div>
                  <div>
                    <label className="text-sm font-medium">Lieu du chantier *</label>
                    <input className="w-full p-2 border rounded mt-1" placeholder="Adresse ou parcelle"
                      onChange={(e) => setNewDocData({...newDocData, lieu_chantier: e.target.value})} />
                  </div>
                  <div>
                    <label className="text-sm font-medium">Date intervention *</label>
                    <input type="date" className="w-full p-2 border rounded mt-1"
                      onChange={(e) => setNewDocData({...newDocData, date_intervention: e.target.value})} />
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <label className="text-sm font-medium">Heure début</label>
                      <input type="time" className="w-full p-2 border rounded mt-1"
                        onChange={(e) => setNewDocData({...newDocData, heure_debut: e.target.value})} />
                    </div>
                    <div>
                      <label className="text-sm font-medium">Heure fin</label>
                      <input type="time" className="w-full p-2 border rounded mt-1"
                        onChange={(e) => setNewDocData({...newDocData, heure_fin: e.target.value})} />
                    </div>
                  </div>
                  <div className="md:col-span-2">
                    <label className="text-sm font-medium">Travaux réalisés</label>
                    <textarea className="w-full p-2 border rounded mt-1" rows="2" placeholder="Labour, semis, récolte..."
                      onChange={(e) => setNewDocData({...newDocData, travaux_realises: e.target.value.split(',').map(t => t.trim())})} />
                  </div>
                  <div className="md:col-span-2">
                    <label className="text-sm font-medium">Matériel utilisé</label>
                    <textarea className="w-full p-2 border rounded mt-1" rows="2" placeholder="Tracteur, charrue, semoir..."
                      onChange={(e) => setNewDocData({...newDocData, materiel_utilise: e.target.value.split(',').map(t => t.trim())})} />
                  </div>
                  <div className="md:col-span-2">
                    <label className="text-sm font-medium">Observations / Réserves</label>
                    <textarea className="w-full p-2 border rounded mt-1" rows="2" placeholder="Observations éventuelles..."
                      onChange={(e) => setNewDocData({...newDocData, observations: e.target.value})} />
                  </div>
                </div>
                <div className="flex gap-2 mt-4">
                  <Button className="bg-green-600 hover:bg-green-700" onClick={() => creerBonReceptionChantier(newDocData)}>
                    <FileSignature className="h-4 w-4 mr-2" /> Créer le Bon
                  </Button>
                  <Button variant="outline" onClick={() => setShowNewDocForm(null)}>Annuler</Button>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Formulaire Étiquette */}
          {showNewEtiquetteForm && (
            <Card className="border-purple-300">
              <CardHeader>
                <CardTitle className="flex items-center justify-between">
                  <span className="flex items-center gap-2">
                    <Tag className="h-5 w-5 text-purple-600" />
                    Nouvelle Étiquette Produit
                  </span>
                  <Button variant="ghost" size="sm" onClick={() => setShowNewEtiquetteForm(false)}>✕</Button>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium">Nom du produit *</label>
                    <input className="w-full p-2 border rounded mt-1" placeholder="Ex: Huile essentielle de lavande"
                      onChange={(e) => setNewDocData({...newDocData, nom_produit: e.target.value})} />
                  </div>
                  <div>
                    <label className="text-sm font-medium">Type de produit *</label>
                    <select className="w-full p-2 border rounded mt-1"
                      onChange={(e) => setNewDocData({...newDocData, type_produit: e.target.value})}>
                      <option value="standard">Standard</option>
                      <option value="bio">Bio</option>
                      <option value="fruits_legumes">Fruits & Légumes</option>
                      <option value="huiles_essentielles">Huiles Essentielles</option>
                      <option value="plantes_ppam">Plantes PPAM</option>
                      <option value="vin">Vin</option>
                      <option value="cereales">Céréales</option>
                    </select>
                  </div>
                  <div>
                    <label className="text-sm font-medium">Poids / Volume *</label>
                    <input className="w-full p-2 border rounded mt-1" placeholder="Ex: 10ml, 500g"
                      onChange={(e) => setNewDocData({...newDocData, poids_volume: e.target.value})} />
                  </div>
                  <div>
                    <label className="text-sm font-medium">N° de Lot *</label>
                    <input className="w-full p-2 border rounded mt-1" placeholder="Ex: LOT2025-001"
                      onChange={(e) => setNewDocData({...newDocData, numero_lot: e.target.value})} />
                  </div>
                  <div>
                    <label className="text-sm font-medium">DLUO / DDM</label>
                    <input type="date" className="w-full p-2 border rounded mt-1"
                      onChange={(e) => setNewDocData({...newDocData, dluo_ddm: e.target.value})} />
                  </div>
                  <div>
                    <label className="text-sm font-medium">Origine</label>
                    <input className="w-full p-2 border rounded mt-1" placeholder="France, UE, etc."
                      onChange={(e) => setNewDocData({...newDocData, origine: e.target.value})} />
                  </div>
                  <div className="md:col-span-2">
                    <label className="text-sm font-medium">Certifications & Labels</label>
                    <div className="flex flex-wrap gap-2 mt-1">
                      {['fr_bio', 'ab', 'hve', 'igp', 'aop', 'label_rouge'].map(cert => (
                        <label key={cert} className="flex items-center gap-1 px-2 py-1 border rounded cursor-pointer hover:bg-gray-50">
                          <input type="checkbox" onChange={(e) => {
                            const certs = newDocData.certifications || [];
                            if (e.target.checked) {
                              setNewDocData({...newDocData, certifications: [...certs, cert]});
                            } else {
                              setNewDocData({...newDocData, certifications: certs.filter(c => c !== cert)});
                            }
                          }} />
                          <span className="text-xs uppercase">{cert.replace('_', ' ')}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Modèles prédéfinis */}
                <div className="mt-4 p-3 bg-purple-50 rounded-lg">
                  <h5 className="font-medium text-sm mb-2">Modèles prédéfinis</h5>
                  <div className="flex flex-wrap gap-2">
                    {modelesEtiquettes.map(modele => (
                      <Button key={modele.id} size="sm" variant="outline" onClick={() => {
                        setNewDocData({
                          ...newDocData,
                          type_produit: modele.type_produit,
                          certifications: modele.certifications
                        });
                      }}>
                        {modele.nom}
                      </Button>
                    ))}
                  </div>
                </div>

                <div className="flex gap-2 mt-4">
                  <Button className="bg-purple-600 hover:bg-purple-700" onClick={() => creerEtiquette(newDocData)}>
                    <Printer className="h-4 w-4 mr-2" /> Générer l'Étiquette
                  </Button>
                  <Button variant="outline" onClick={() => setShowNewEtiquetteForm(false)}>Annuler</Button>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Liste des documents récents */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center justify-between">
                <span>Documents récents</span>
                <span className="text-sm font-normal text-gray-500">{documents.length} document(s)</span>
              </CardTitle>
            </CardHeader>
            <CardContent>
              {documents.length > 0 ? (
                <div className="space-y-2">
                  {documents.slice(0, 10).map((doc, idx) => (
                    <div key={doc.id || idx} className="flex items-center justify-between p-3 border rounded-lg hover:bg-gray-50">
                      <div className="flex items-center gap-3">
                        {doc.type === 'bon_livraison' && <Package className="h-5 w-5 text-blue-500" />}
                        {doc.type === 'lettre_voiture' && <Truck className="h-5 w-5 text-orange-500" />}
                        {doc.type === 'bon_reception_chantier' && <ClipboardCheck className="h-5 w-5 text-green-500" />}
                        <div>
                          <p className="font-medium">{doc.numero}</p>
                          <p className="text-xs text-gray-500">
                            {doc.destinataire?.nom || doc.client?.nom} - {new Date(doc.created_at).toLocaleDateString('fr-FR')}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className={`px-2 py-1 rounded text-xs ${
                          doc.statut === 'signe' ? 'bg-green-100 text-green-700' :
                          doc.statut === 'emis' ? 'bg-blue-100 text-blue-700' :
                          'bg-amber-100 text-amber-700'
                        }`}>
                          {doc.statut}
                        </span>
                        <Button size="sm" variant="ghost"><Printer className="h-4 w-4" /></Button>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-gray-500">
                  <FileText className="h-12 w-12 mx-auto mb-2 opacity-30" />
                  <p>Aucun document créé</p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Liste des étiquettes */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center justify-between">
                <span className="flex items-center gap-2">
                  <Tag className="h-5 w-5 text-purple-600" />
                  Étiquettes produits
                </span>
                <span className="text-sm font-normal text-gray-500">{etiquettes.length} étiquette(s)</span>
              </CardTitle>
            </CardHeader>
            <CardContent>
              {etiquettes.length > 0 ? (
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3">
                  {etiquettes.map((etiq, idx) => (
                    <div key={etiq.id || idx} className="p-3 border rounded-lg hover:shadow-md transition-shadow">
                      <div className="flex items-start justify-between">
                        <div>
                          <p className="font-medium">{etiq.nom_produit}</p>
                          <p className="text-xs text-gray-500">{etiq.poids_volume} - Lot: {etiq.numero_lot}</p>
                        </div>
                        <Button size="sm" variant="ghost"><Printer className="h-4 w-4" /></Button>
                      </div>
                      <div className="flex flex-wrap gap-1 mt-2">
                        {etiq.logos_certifications?.map((logo, i) => (
                          <span key={i} className="px-1.5 py-0.5 bg-green-100 text-green-700 text-[10px] rounded">
                            {logo.code}
                          </span>
                        ))}
                      </div>
                      {etiq.mentions_obligatoires?.length > 0 && (
                        <p className="text-[10px] text-gray-400 mt-1 truncate">
                          {etiq.mentions_obligatoires[0]}
                        </p>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-gray-500">
                  <Tag className="h-12 w-12 mx-auto mb-2 opacity-30" />
                  <p>Aucune étiquette créée</p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      )}

      {/* ============ TAB ALERTES ============ */}
      {activeTab === 'alertes' && (
        <div>
          {/* Filtres */}
          <div className="flex gap-2 mb-4">
            <Button variant={filtreAlerte === 'toutes' ? 'default' : 'outline'} size="sm" onClick={() => setFiltreAlerte('toutes')}>
              Toutes
            </Button>
            <Button variant={filtreAlerte === 'pac' ? 'default' : 'outline'} size="sm" onClick={() => setFiltreAlerte('pac')}>
              PAC
            </Button>
            <Button variant={filtreAlerte === 'bio' ? 'default' : 'outline'} size="sm" onClick={() => setFiltreAlerte('bio')}>
              Bio
            </Button>
            <Button variant={filtreAlerte === 'phyto' ? 'default' : 'outline'} size="sm" onClick={() => setFiltreAlerte('phyto')}>
              Phyto
            </Button>
            <Button variant={filtreAlerte === 'exploitation' ? 'default' : 'outline'} size="sm" onClick={() => setFiltreAlerte('exploitation')}>
              Exploitation
            </Button>
          </div>

          <div className="space-y-3">
            {filteredAlertes.map(alerte => (
              <Card key={alerte.id} className={`border-l-4 ${
                alerte.priorite === 'haute' ? 'border-l-red-500' :
                alerte.priorite === 'moyenne' ? 'border-l-amber-500' : 'border-l-green-500'
              }`}>
                <CardContent className="py-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className={`p-2 rounded-lg ${getPrioriteColor(alerte.priorite)}`}>
                        <Bell className="h-4 w-4" />
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <p className="font-medium">{alerte.titre}</p>
                          <span className="text-xs bg-gray-100 px-2 py-0.5 rounded">{alerte.categorie}</span>
                        </div>
                        <p className="text-sm text-gray-600">{alerte.message}</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-sm font-medium">{alerte.date}</p>
                      <span className={`text-xs px-2 py-1 rounded-full ${getPrioriteColor(alerte.priorite)}`}>
                        {alerte.priorite}
                      </span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      )}

      {/* ============ TAB CALENDRIER CONFORMITÉ ============ */}
      {activeTab === 'conformite' && (
        <div className="space-y-4" data-testid="tab-conformite-content">
          <Card className="border-amber-200 bg-amber-50/30">
            <CardContent className="py-4 flex items-center justify-between flex-wrap gap-3">
              <div>
                <p className="font-semibold text-amber-900">Calendrier de conformité automatique</p>
                <p className="text-sm text-amber-800">
                  Génère automatiquement les échéances réglementaires (CVI, DRM, CIHEF, PAC, DUER, registre phyto...) selon l'activité de votre société.
                </p>
              </div>
              <div className="flex gap-2 flex-wrap">
                <Button data-testid="btn-conformite-generer" size="sm" onClick={() => genererConformite(false)} disabled={conformiteLoading}>
                  <Plus className="h-4 w-4 mr-2" />Générer les échéances
                </Button>
                <Button data-testid="btn-conformite-regenerer" size="sm" variant="outline" onClick={() => genererConformite(true)} disabled={conformiteLoading}>
                  <RefreshCw className="h-4 w-4 mr-2" />Régénérer tout
                </Button>
                <Button data-testid="btn-conformite-pdf" size="sm" variant="outline" onClick={async () => {
                  try {
                    const res = await api.get(`/reglementaire/alertes/export/pdf?annee=${new Date().getFullYear()}&inclure_faites=true`, { responseType: 'blob' });
                    const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
                    const link = document.createElement('a');
                    link.href = url;
                    link.setAttribute('download', `calendrier_conformite_${new Date().getFullYear()}.pdf`);
                    document.body.appendChild(link);
                    link.click();
                    link.remove();
                    window.URL.revokeObjectURL(url);
                    toast.success('PDF téléchargé');
                  } catch (e) {
                    toast.error('Erreur export PDF');
                  }
                }}>
                  <Download className="h-4 w-4 mr-2" />Export PDF annuel
                </Button>
              </div>
            </CardContent>
          </Card>

          {conformiteLoading && <p className="text-sm text-gray-500">Chargement...</p>}

          {!conformiteLoading && conformiteAlertes.length === 0 && (
            <Card data-testid="conformite-empty">
              <CardContent className="py-10 text-center text-gray-500">
                Aucune échéance pour le moment. Cliquez sur "Générer les échéances" pour démarrer.
              </CardContent>
            </Card>
          )}

          <div className="space-y-2" data-testid="conformite-list">
            {conformiteAlertes.map(a => {
              const ech = a.date_echeance ? new Date(a.date_echeance) : null;
              const today = new Date();
              const jours = ech ? Math.ceil((ech - today) / (1000 * 60 * 60 * 24)) : null;
              const enRetard = a.statut === 'en_retard' || (jours !== null && jours < 0 && a.statut !== 'fait');
              const urgent = jours !== null && jours >= 0 && jours <= 15;
              const borderColor = a.statut === 'fait' ? 'border-l-green-500 opacity-60' :
                enRetard ? 'border-l-red-600' :
                urgent ? 'border-l-amber-500' :
                a.criticite === 'haute' ? 'border-l-blue-500' : 'border-l-gray-300';
              return (
                <Card key={a.id} className={`border-l-4 ${borderColor}`} data-testid={`conformite-item-${a.id}`}>
                  <CardContent className="py-3">
                    <div className="flex items-center justify-between gap-3 flex-wrap">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <p className="font-medium">{a.titre}</p>
                          <span className="text-xs px-2 py-0.5 rounded bg-gray-100">{a.metier}</span>
                          <span className={`text-xs px-2 py-0.5 rounded ${a.criticite === 'haute' ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-700'}`}>
                            {a.criticite}
                          </span>
                          {a.recurrence && (
                            <span className="text-xs px-2 py-0.5 rounded bg-blue-50 text-blue-700">{a.recurrence}</span>
                          )}
                        </div>
                        <p className="text-sm text-gray-600 mt-1">{a.description}</p>
                        {a.portail && (
                          <a href={a.portail} target="_blank" rel="noopener noreferrer" className="text-xs text-blue-600 hover:underline inline-flex items-center gap-1 mt-1">
                            <ExternalLink className="h-3 w-3" />{a.portail}
                          </a>
                        )}
                      </div>
                      <div className="text-right shrink-0">
                        <p className="text-sm font-medium">
                          {ech ? ech.toLocaleDateString('fr-FR') : '-'}
                        </p>
                        {jours !== null && (
                          <p className={`text-xs font-medium ${enRetard ? 'text-red-600' : urgent ? 'text-amber-600' : 'text-gray-500'}`}>
                            {enRetard ? `⚠️ En retard de ${-jours}j` : jours === 0 ? `🔥 Aujourd'hui` : `J-${jours}`}
                          </p>
                        )}
                        <div className="flex gap-1 mt-2 justify-end">
                          {a.statut !== 'fait' && (
                            <Button data-testid={`btn-fait-${a.id}`} size="sm" variant="outline" className="h-7 text-xs" onClick={() => marquerConformiteStatut(a.id, 'fait')}>
                              <CheckCircle className="h-3 w-3 mr-1" />Fait
                            </Button>
                          )}
                          {a.statut === 'fait' && (
                            <Button data-testid={`btn-reouvrir-${a.id}`} size="sm" variant="outline" className="h-7 text-xs" onClick={() => marquerConformiteStatut(a.id, 'a_venir')}>
                              Rouvrir
                            </Button>
                          )}
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      )}

      {/* ============ TAB CARBONE & ÉNERGIE ============ */}
      {activeTab === 'carbone' && (
        <div className="space-y-6">
          {/* Header Carbone */}
          <Card className="border-emerald-300 bg-gradient-to-r from-emerald-50 to-green-50">
            <CardContent className="py-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="p-3 bg-emerald-600 rounded-xl">
                    <Flame className="h-6 w-6 text-white" />
                  </div>
                  <div>
                    <h3 className="font-bold text-lg">Module Carbone Stratégique</h3>
                    <p className="text-sm text-gray-600">Bilan carbone, Label Bas Carbone, méthanisation, pyrolyse</p>
                  </div>
                </div>
                <Button onClick={lancerSimulation} disabled={loadingCarbone} className="bg-emerald-600 hover:bg-emerald-700">
                  <Play className="h-4 w-4 mr-2" />
                  {loadingCarbone ? 'Calcul...' : 'Lancer Simulation'}
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* KPIs Carbone */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Card className="bg-gradient-to-br from-red-50 to-orange-50 border-red-200">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <TrendingUp className="h-8 w-8 text-red-500" />
                  <div>
                    <p className="text-2xl font-bold text-red-700">
                      {carboneDashboard?.bilan?.emissions_totales?.toFixed(0) || '---'}
                    </p>
                    <p className="text-xs text-red-600">tCO2e émises/an</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card className="bg-gradient-to-br from-green-50 to-emerald-50 border-green-200">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <TrendingDown className="h-8 w-8 text-green-500" />
                  <div>
                    <p className="text-2xl font-bold text-green-700">
                      {carboneDashboard?.bilan?.sequestration_totale?.toFixed(0) || '---'}
                    </p>
                    <p className="text-xs text-green-600">tCO2e séquestrées</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card className="bg-gradient-to-br from-blue-50 to-cyan-50 border-blue-200">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <Target className="h-8 w-8 text-blue-500" />
                  <div>
                    <p className="text-2xl font-bold text-blue-700">
                      {carboneDashboard?.bilan?.bilan_net?.toFixed(0) || '---'}
                    </p>
                    <p className="text-xs text-blue-600">Bilan net tCO2e</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card className="bg-gradient-to-br from-amber-50 to-yellow-50 border-amber-200">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <PiggyBank className="h-8 w-8 text-amber-500" />
                  <div>
                    <p className="text-2xl font-bold text-amber-700">
                      {carboneDashboard?.potentiel_revenu?.toLocaleString() || '---'} €
                    </p>
                    <p className="text-xs text-amber-600">Potentiel crédits</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Scénarios & Simulation */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <BarChart3 className="h-5 w-5 text-emerald-600" />
                Simulation de Scénarios
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex gap-2 mb-4">
                <Button 
                  variant={simulationScenario === 'conservateur' ? 'default' : 'outline'} 
                  size="sm"
                  onClick={() => setSimulationScenario('conservateur')}
                >
                  Conservateur
                </Button>
                <Button 
                  variant={simulationScenario === 'optimise' ? 'default' : 'outline'} 
                  size="sm"
                  onClick={() => setSimulationScenario('optimise')}
                  className={simulationScenario === 'optimise' ? 'bg-emerald-600' : ''}
                >
                  Optimisé
                </Button>
                <Button 
                  variant={simulationScenario === 'ambitieux' ? 'default' : 'outline'} 
                  size="sm"
                  onClick={() => setSimulationScenario('ambitieux')}
                >
                  Ambitieux
                </Button>
              </div>

              {carboneSimulation ? (
                <div className="grid md:grid-cols-3 gap-4">
                  {carboneSimulation.scenarios?.map((scenario, idx) => (
                    <Card key={idx} className={`${scenario.nom === simulationScenario ? 'border-emerald-500 border-2' : ''}`}>
                      <CardContent className="p-4">
                        <h4 className="font-bold capitalize mb-3">{scenario.nom}</h4>
                        <div className="space-y-2 text-sm">
                          <div className="flex justify-between">
                            <span className="text-gray-600">Réduction CO2</span>
                            <span className="font-bold text-green-600">-{scenario.reduction_co2?.toFixed(0)} t</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-600">Crédits générés</span>
                            <span className="font-bold">{scenario.credits_generes?.toFixed(0)} t</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-600">Revenu estimé</span>
                            <span className="font-bold text-amber-600">{scenario.revenu_estime?.toLocaleString()} €</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-600">Investissement</span>
                            <span className="font-medium">{scenario.investissement?.toLocaleString()} €</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-600">ROI</span>
                            <span className="font-bold text-blue-600">{scenario.roi_annees} ans</span>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-gray-500">
                  <BarChart3 className="h-12 w-12 mx-auto mb-2 opacity-30" />
                  <p>Cliquez sur "Lancer Simulation" pour calculer les scénarios</p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Actions disponibles - Métiers */}
          <div className="grid md:grid-cols-3 gap-4">
            <Card className="hover:shadow-lg transition-shadow cursor-pointer" onClick={() => creerDossierCarbone('label_bas_carbone')}>
              <CardContent className="p-4">
                <div className="flex items-center gap-3 mb-3">
                  <div className="p-2 bg-green-100 rounded-lg">
                    <Award className="h-6 w-6 text-green-600" />
                  </div>
                  <div>
                    <h4 className="font-bold">Label Bas Carbone</h4>
                    <p className="text-xs text-gray-500">Certification officielle</p>
                  </div>
                </div>
                <p className="text-sm text-gray-600 mb-3">
                  Valorisez vos pratiques agricoles via le label officiel français.
                </p>
                <div className="flex items-center justify-between">
                  <span className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded">30-50€/tCO2</span>
                  <ChevronRight className="h-4 w-4 text-gray-400" />
                </div>
              </CardContent>
            </Card>

            <Card className="hover:shadow-lg transition-shadow cursor-pointer" onClick={() => creerDossierCarbone('methanisation')}>
              <CardContent className="p-4">
                <div className="flex items-center gap-3 mb-3">
                  <div className="p-2 bg-blue-100 rounded-lg">
                    <Factory className="h-6 w-6 text-blue-600" />
                  </div>
                  <div>
                    <h4 className="font-bold">Méthanisation</h4>
                    <p className="text-xs text-gray-500">Production biogaz</p>
                  </div>
                </div>
                <p className="text-sm text-gray-600 mb-3">
                  Transformez vos déchets organiques en énergie et revenus.
                </p>
                <div className="flex items-center justify-between">
                  <span className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded">Tarif rachat garanti</span>
                  <ChevronRight className="h-4 w-4 text-gray-400" />
                </div>
              </CardContent>
            </Card>

            <Card className="hover:shadow-lg transition-shadow cursor-pointer" onClick={() => creerDossierCarbone('pyrolyse_biochar')}>
              <CardContent className="p-4">
                <div className="flex items-center gap-3 mb-3">
                  <div className="p-2 bg-orange-100 rounded-lg">
                    <Flame className="h-6 w-6 text-orange-600" />
                  </div>
                  <div>
                    <h4 className="font-bold">Pyrolyse / Biochar</h4>
                    <p className="text-xs text-gray-500">Séquestration durable</p>
                  </div>
                </div>
                <p className="text-sm text-gray-600 mb-3">
                  Produisez du biochar pour améliorer vos sols et stocker du carbone.
                </p>
                <div className="flex items-center justify-between">
                  <span className="text-xs bg-orange-100 text-orange-700 px-2 py-1 rounded">100-150€/tCO2</span>
                  <ChevronRight className="h-4 w-4 text-gray-400" />
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Dossiers en cours */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center justify-between">
                <span className="flex items-center gap-2">
                  <FileSpreadsheet className="h-5 w-5 text-emerald-600" />
                  Mes Dossiers Carbone
                </span>
                <span className="text-sm font-normal text-gray-500">{carboneDossiers.length} dossier(s)</span>
              </CardTitle>
            </CardHeader>
            <CardContent>
              {carboneDossiers.length > 0 ? (
                <div className="space-y-3">
                  {carboneDossiers.map((dossier, idx) => (
                    <div key={dossier.id || idx} className="flex items-center justify-between p-4 border rounded-lg hover:bg-gray-50">
                      <div className="flex items-center gap-4">
                        <div className={`p-2 rounded-lg ${
                          dossier.type_dossier?.includes('carbone') ? 'bg-green-100' :
                          dossier.type_dossier?.includes('metha') ? 'bg-blue-100' : 'bg-orange-100'
                        }`}>
                          {dossier.type_dossier?.includes('carbone') ? <Award className="h-5 w-5 text-green-600" /> :
                           dossier.type_dossier?.includes('metha') ? <Factory className="h-5 w-5 text-blue-600" /> :
                           <Flame className="h-5 w-5 text-orange-600" />}
                        </div>
                        <div>
                          <p className="font-medium">{dossier.titre || dossier.type_dossier}</p>
                          <p className="text-sm text-gray-500">Créé le {new Date(dossier.created_at).toLocaleDateString('fr-FR')}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                          dossier.statut === 'valide' || dossier.statut === 'paiement_recu' ? 'bg-green-100 text-green-700' :
                          dossier.statut === 'depose' || dossier.statut === 'en_cours' ? 'bg-blue-100 text-blue-700' :
                          'bg-amber-100 text-amber-700'
                        }`}>
                          {dossier.statut || 'En préparation'}
                        </span>
                        {dossier.montant_estime && (
                          <span className="font-bold text-emerald-600">{dossier.montant_estime.toLocaleString()} €</span>
                        )}
                        <ChevronRight className="h-4 w-4 text-gray-400" />
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-gray-500">
                  <FileSpreadsheet className="h-12 w-12 mx-auto mb-2 opacity-30" />
                  <p>Aucun dossier carbone en cours</p>
                  <p className="text-sm">Cliquez sur une action ci-dessus pour créer un dossier</p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Recommandations IA */}
          {carboneRecommandations.length > 0 && (
            <Card className="border-purple-200 bg-purple-50/30">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <TreePine className="h-5 w-5 text-purple-600" />
                  Recommandations IA
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {carboneRecommandations.slice(0, 5).map((reco, idx) => (
                    <div key={idx} className="flex items-start gap-3 p-3 bg-white rounded-lg">
                      <CheckCircle className="h-5 w-5 text-purple-500 mt-0.5" />
                      <div>
                        <p className="font-medium">{reco.titre || reco.action}</p>
                        <p className="text-sm text-gray-600">{reco.description}</p>
                        {reco.potentiel_co2 && (
                          <span className="text-xs text-purple-600 font-medium">
                            Potentiel: -{reco.potentiel_co2} tCO2/an
                          </span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      )}
    </div>
  );
}
